/**
 * Hook to create a new absence request
 * Handles both HR creating for others and employees creating for themselves
 * TypeScript version
 */

import api from "@lib/api";
import { queryKeys } from "@lib/api/queryKeys";
import { useToast } from "@lib/hooks/useToast";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { homeDashboardQueryKeys } from "@/features/home-dashboard";
import { congesDashboardQueryKeys } from "@/features/home-dashboard/conges/hooks/congesDashboardQueryKeys";
import { API_ENDPOINTS } from "../../config/constants";

interface CreateAbsenceResponse {
  etatDemande?: {
    code: string;
    libelle: string;
  };
  id?: number;
}

interface UseCreateAbsenceReturn {
  create: (data: Record<string, unknown>, options?: { onSuccess?: () => void }) => void;
  createAsync: (data: Record<string, unknown>) => Promise<CreateAbsenceResponse>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  data: CreateAbsenceResponse | null;
  reset: () => void;
}

export function useCreateAbsence(): UseCreateAbsenceReturn {
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation: UseMutationResult<
    CreateAbsenceResponse,
    Error,
    Record<string, unknown>
  > = useMutation({
    mutationFn: async (data: Record<string, unknown>): Promise<CreateAbsenceResponse> => {
      const response = await api.post(API_ENDPOINTS.DEMANDE_ABSENCES.LIST, data);
      return response.data as CreateAbsenceResponse;
    },
    onSuccess: (data: CreateAbsenceResponse) => {
      // Invalidate absences list
      queryClient.invalidateQueries({ queryKey: queryKeys.rh.absences.all() });

      // Invalidate time counters (a validated hours/leave request consumes them)
      queryClient.invalidateQueries({ queryKey: queryKeys.rh.compteursTemps.all() });

      // Invalidate dashboard queries to update the table
      queryClient.invalidateQueries({
        queryKey: congesDashboardQueryKeys.summary(),
      });

      // Home dashboard summary carries the CP/RTT/overtime balances shown on `/`
      queryClient.invalidateQueries({
        queryKey: homeDashboardQueryKeys.summary(),
      });

      if (data.etatDemande?.code === "BROUILLON") {
        toast.info("Demande créée en brouillon");
      } else {
        toast.success("Demande soumise avec succès");
      }
    },
    onError: (error: Error) => {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message || error.message;
      toast.error(`Erreur lors de la création: ${message}`);
    },
  });

  return {
    create: mutation.mutate,
    createAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data ?? null,
    reset: mutation.reset,
  };
}
